import type { TFunction } from 'i18next';
import type { NpsQuestion } from '@/shared/types/nps';

/** Agrupa perguntas TEXT (input) consecutivas no início — dados do usuário em um único step. */
export function partitionSurveyQuestions(questions: NpsQuestion[]) {
  const sorted = [...questions].sort((a, b) => a.position - b.position);
  const userInfo: NpsQuestion[] = [];
  let i = 0;

  while (i < sorted.length) {
    const q = sorted[i];
    const isSingleLineText =
      q.response_type === 'TEXT' && q.response_metadata?.input_type !== 'textarea';
    if (isSingleLineText && userInfo.length === i) {
      userInfo.push(q);
      i++;
    } else {
      break;
    }
  }

  return { userInfo, rest: sorted.slice(i) };
}

/** inputMode / type por título da pergunta (heurística mobile). */
export function contactFieldKind(title: string): 'email' | 'phone' | 'name' | 'text' {
  const t = title.toLowerCase();
  if (
    t.includes('e-mail') ||
    t.includes('email') ||
    t.includes('correo') ||
    t.includes('correio')
  ) {
    return 'email';
  }
  if (
    t.includes('celular') ||
    t.includes('telefone') ||
    t.includes('whatsapp') ||
    t.includes('phone') ||
    t.includes('mobile') ||
    t.includes('móvil') ||
    t.includes('movil') ||
    t.includes('telemóvel') ||
    t.includes('telemovel')
  ) {
    return 'phone';
  }
  if (t.includes('nome') || t.includes('name') || t.includes('nombre')) return 'name';
  return 'text';
}

export function contactInputProps(
  title: string,
  t: TFunction,
): {
  kind: ReturnType<typeof contactFieldKind>;
  type?: string;
  inputMode?: 'text' | 'email' | 'tel' | 'numeric';
  autoComplete?: string;
  placeholder?: string;
} {
  const kind = contactFieldKind(title);
  if (kind === 'email') {
    return {
      kind,
      type: 'email',
      inputMode: 'email',
      autoComplete: 'email',
      placeholder: t('placeholders.email'),
    };
  }
  if (kind === 'phone') {
    return {
      kind,
      type: 'tel',
      inputMode: 'tel',
      autoComplete: 'tel',
      placeholder: t('placeholders.phone'),
    };
  }
  if (kind === 'name') {
    return {
      kind,
      type: 'text',
      inputMode: 'text',
      autoComplete: 'name',
      placeholder: t('placeholders.name'),
    };
  }
  return { kind, type: 'text', inputMode: 'text', placeholder: t('placeholders.text') };
}

export function phoneDigits(value: string): string {
  return value.replace(/\D/g, '').slice(0, 11);
}

/** Máscara brasileira: (11) 99999-9999 ou (11) 9999-9999 */
export function maskPhone(value: string): string {
  const d = phoneDigits(value);
  if (d.length === 0) return '';
  if (d.length <= 2) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

export function isValidPhone(value: string): boolean {
  const digits = phoneDigits(value);
  return digits.length === 10 || digits.length === 11;
}

const EMAIL_RE =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

export function isValidEmail(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > 254) return false;
  if (!EMAIL_RE.test(trimmed)) return false;
  const [, domain] = trimmed.split('@');
  if (!domain || domain.length < 4 || !domain.includes('.')) return false;
  const tld = domain.split('.').pop();
  return Boolean(tld && tld.length >= 2);
}
